import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toSafeMillimeters = (value, fallback, min = 15, max = 120) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(Math.max(size, min), max);
};

const LABEL_PRINTER_SIZE_KEY = "pharmacy.label-printer-size";
// The compact horizontal label shown on the XP-T361U roll.
const DEFAULT_LABEL_PRINTER_SIZE = { widthMm: 50, heightMm: 25 };
const DEFAULT_LABEL_PRINTER_SETTINGS = {
  ...DEFAULT_LABEL_PRINTER_SIZE,
  qrSizeMm: 18,
  topMarginMm: 2,
  bottomMarginMm: 2,
  leftMarginMm: 2,
  rightMarginMm: 2,
  copies: 1,
};

const toSafeNumber = (value, fallback, min, max) =>
  toSafeMillimeters(value, fallback, min, max);

const sanitizeLabelPrinterSettings = (settings, fallback = DEFAULT_LABEL_PRINTER_SETTINGS) => {
  const widthMm = toSafeNumber(settings?.widthMm, fallback.widthMm, 20, 80);
  const heightMm = toSafeNumber(settings?.heightMm, fallback.heightMm, 15, 120);
  // Keep at least 5 mm of printable space on each axis. This prevents invalid
  // combined margins from leaving no room for the QR image.
  const maxHorizontalMargins = Math.max(0, widthMm - 5);
  const leftMarginMm = Math.min(
    toSafeNumber(settings?.leftMarginMm, fallback.leftMarginMm, 0, 20),
    maxHorizontalMargins,
  );
  const rightMarginMm = Math.min(
    toSafeNumber(settings?.rightMarginMm, fallback.rightMarginMm, 0, 20),
    maxHorizontalMargins - leftMarginMm,
  );
  const maxVerticalMargins = Math.max(0, heightMm - 5);
  const topMarginMm = Math.min(
    toSafeNumber(settings?.topMarginMm, fallback.topMarginMm, 0, 20),
    maxVerticalMargins,
  );
  const bottomMarginMm = Math.min(
    toSafeNumber(settings?.bottomMarginMm, fallback.bottomMarginMm, 0, 20),
    maxVerticalMargins - topMarginMm,
  );

  return {
    widthMm,
    heightMm,
    qrSizeMm: toSafeNumber(settings?.qrSizeMm, fallback.qrSizeMm, 5, 75),
    topMarginMm,
    bottomMarginMm,
    leftMarginMm,
    rightMarginMm,
    copies: toSafeNumber(settings?.copies, fallback.copies, 1, 100),
  };
};

export const getLabelPrinterSettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LABEL_PRINTER_SIZE_KEY));
    if (saved) return sanitizeLabelPrinterSettings(saved);
  } catch {
    // Use defaults when storage is unavailable or invalid.
  }
  return DEFAULT_LABEL_PRINTER_SETTINGS;
};

export const setLabelPrinterSettings = (settings) => {
  const previous = getLabelPrinterSettings();
  const next = sanitizeLabelPrinterSettings(settings, previous);
  try {
    window.localStorage.setItem(LABEL_PRINTER_SIZE_KEY, JSON.stringify(next));
  } catch {
    // Printing still uses the selected settings if storage is unavailable.
  }
  return next;
};

export const getLabelPrinterSize = () => {
  const { widthMm, heightMm } = getLabelPrinterSettings();
  return { widthMm, heightMm };
};

export const setLabelPrinterSize = (widthMm, heightMm) => {
  const settings = setLabelPrinterSettings({ widthMm, heightMm });
  return { widthMm: settings.widthMm, heightMm: settings.heightMm };
};

const buildBarcodeSvg = (value) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svg, String(value), {
    format: "CODE128",
    height: 34,
    width: 1,
    displayValue: false,
    margin: 0,
    background: "#ffffff",
    lineColor: "#000000",
  });
  return svg.outerHTML;
};

const createLabelMarkup = async ({ barcode, name, mode = "both" }, settings) => {
  const code = String(barcode || "").trim();
  if (!code) return "";

  const safeMode = ["barcode", "qr", "both"].includes(mode) ? mode : "both";
  const safeName = escapeHtml(name || code);
  const safeBarcode = escapeHtml(code);
  const showBarcode = safeMode === "barcode" || safeMode === "both";
  const showQr = safeMode === "qr" || safeMode === "both";
  // A 20--35 mm label does not have enough space for two readable codes.
  // When both are requested, print a barcode label and a QR label rather than
  // shrinking or clipping either one.
  const isCompact =
    settings.widthMm < 38 ||
    (settings.widthMm < 48 && settings.heightMm < 25);
  const barcodeSvg = showBarcode ? buildBarcodeSvg(code) : "";
  const qrDataUrl = showQr
    ? await QRCode.toDataURL(code, {
        // Generate more pixels than the printer needs. The browser then scales
        // down cleanly for 203 dpi thermal printers such as the XP-T361U.
        width: 256,
        margin: 2,
        color: { dark: "#000000", light: "#ffffff" },
      })
    : "";

  const renderLabel = (labelMode, contents) => `
    <section class="label mode-${labelMode}${isCompact ? " compact" : ""}">
      <div class="title">${safeName}</div>
      <div class="codes">${contents}</div>
      <div class="code-text">${safeBarcode}</div>
    </section>
  `;

  // QR-only printing is deliberately separate from product labels: it contains
  // exactly one image and no name, barcode, or other printable text.
  if (safeMode === "qr") {
    return `
      <section class="label qr-only">
        <div class="qr-print-area">
          <img class="qr" src="${qrDataUrl}" alt="QR code" />
        </div>
      </section>
    `;
  }

  // A label intentionally contains no stock, price, expiry, page, or app details.
  if (isCompact && safeMode === "both") {
    return (
      renderLabel("barcode", `<div class="barcode">${barcodeSvg}</div>`) +
      renderLabel("qr", `<img class="qr" src="${qrDataUrl}" alt="QR ${safeBarcode}" />`)
    );
  }

  return `
    ${renderLabel(
      safeMode,
      `${showBarcode ? `<div class="barcode">${barcodeSvg}</div>` : ""}${
        showQr ? `<img class="qr" src="${qrDataUrl}" alt="QR ${safeBarcode}" />` : ""
      }`,
    )}
  `;
};

/**
 * Print labels as individual physical-label pages in one print job.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr'|'both' }[]} labels
 * @param {{ widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabels = async (
  labels,
  { widthMm, heightMm } = {},
) => {
  const printableLabels = (Array.isArray(labels) ? labels : []).filter((label) =>
    Boolean(String(label?.barcode || "").trim()),
  );
  if (!printableLabels.length) return false;

  // XP-T361U supports labels up to 80 mm wide. These values must match the
  // label stock configured in the printer driver.
  const configuredSettings = getLabelPrinterSettings();
  const labelWidthMm = toSafeMillimeters(widthMm, configuredSettings.widthMm, 20, 80);
  const labelHeightMm = toSafeMillimeters(heightMm, configuredSettings.heightMm);
  const printableWidthMm = Math.max(
    1,
    labelWidthMm - configuredSettings.leftMarginMm - configuredSettings.rightMarginMm,
  );
  const printableHeightMm = Math.max(
    1,
    labelHeightMm - configuredSettings.topMarginMm - configuredSettings.bottomMarginMm,
  );
  const qrSizeMm = Math.max(
    1,
    Math.min(configuredSettings.qrSizeMm, printableWidthMm, printableHeightMm),
  );
  const labelSettings = {
    ...configuredSettings,
    widthMm: labelWidthMm,
    heightMm: labelHeightMm,
  };
  const labelsMarkup = (
    await Promise.all(
      printableLabels.map((label) => createLabelMarkup(label, labelSettings)),
    )
  ).join("");

  const win = window.open("", "medicine-label-print");
  if (!win) return false;

  win.document.write(`
    <html>
      <head>
        <title>Medicine labels</title>
        <style>
          @page {
            size: ${labelWidthMm}mm ${labelHeightMm}mm;
            margin: 0;
          }
          * { box-sizing: border-box; }
          html, body {
            width: ${labelWidthMm}mm;
            margin: 0;
            padding: 0;
            background: #ffffff;
          }
          body {
            font-family: Arial, sans-serif;
            color: #000000;
            writing-mode: horizontal-tb;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .label {
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            min-height: ${labelHeightMm}mm;
            max-height: ${labelHeightMm}mm;
            padding: 1mm 1.25mm;
            overflow: hidden;
            text-align: center;
            writing-mode: horizontal-tb;
            background: #ffffff;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: stretch;
            gap: 0.5mm;
            break-after: page;
            page-break-after: always;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .label:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          .qr-only {
            display: block;
            padding: 0;
          }
          .qr-print-area {
            width: 100%;
            height: 100%;
            padding: ${configuredSettings.topMarginMm}mm ${configuredSettings.rightMarginMm}mm ${configuredSettings.bottomMarginMm}mm ${configuredSettings.leftMarginMm}mm;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .qr-only .qr {
            display: block;
            width: ${qrSizeMm}mm;
            height: ${qrSizeMm}mm;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
          .title {
            flex: 0 0 auto;
            font-size: 7px;
            font-weight: 700;
            line-height: 1.1;
            max-height: 4mm;
            overflow: hidden;
            overflow-wrap: anywhere;
          }
          .codes {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 1mm;
            flex-wrap: nowrap;
            flex: 1 1 auto;
            min-height: 0;
            overflow: hidden;
          }
          .barcode {
            flex: 1 1 auto;
            min-width: 0;
            overflow: hidden;
            line-height: 0;
          }
          .barcode svg {
            display: block;
            width: 100%;
            max-width: 100%;
            height: 10mm;
            margin: 0 auto;
          }
          .qr {
            flex: 0 0 auto;
            width: 11mm;
            height: 11mm;
            max-width: 42%;
            object-fit: contain;
            display: block;
          }
          .code-text {
            flex: 0 0 auto;
            font-size: 5px;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .mode-barcode .barcode svg {
            height: 14mm;
          }
          .mode-barcode .codes {
            min-height: 14mm;
          }
          .mode-barcode .code-text,
          .mode-qr .code-text {
            font-size: 5.8px;
          }
          .mode-qr .qr {
            width: 15mm;
            height: 15mm;
            max-width: 70%;
          }
          .compact.mode-barcode .barcode svg {
            height: 10mm;
          }
          .compact.mode-qr .qr {
            width: 12mm;
            height: 12mm;
            max-width: 75%;
          }
          .compact .title {
            font-size: 6px;
          }
          .compact .code-text {
            font-size: 4.5px;
          }
          @media print {
            html, body {
              width: ${labelWidthMm}mm !important;
            }
            .label {
              width: ${labelWidthMm}mm !important;
              height: ${labelHeightMm}mm !important;
              margin: 0 !important;
            }
          }
        </style>
      </head>
      <body>
        ${labelsMarkup}
        <script>
          window.addEventListener("load", () => {
            const printLabel = () => {
              // Keep this document open. Closing a print window programmatically
              // can cancel a Windows print job before the driver has accepted it.
              window.focus();
              window.print();
            };

            const images = Array.from(document.images);
            Promise.all(
              images.map((image) =>
                image.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      image.addEventListener("load", resolve, { once: true });
                      image.addEventListener("error", resolve, { once: true });
                    }),
              ),
            ).then(() => window.setTimeout(printLabel, 100));
          });
        <\/script>
      </body>
    </html>
  `);
  win.document.close();
  return true;
};

/**
 * Print copies of one medicine label. Kept for existing single-label callers.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr'|'both', copies?: number, widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  mode = "both",
  copies,
  widthMm,
  heightMm,
}) => {
  const configuredCopies = getLabelPrinterSettings().copies;
  const labelCount = Math.max(1, Number.parseInt(copies ?? configuredCopies, 10) || 1);
  return printMedicineLabels(
    Array.from({ length: labelCount }, () => ({ barcode, name, mode })),
    { widthMm, heightMm },
  );
};

export default printMedicineLabel;
